/**
 * The vault session in the web app (Phase 9, spec §6.4).
 *
 * - Storage: the ENCRYPTED vault in this browser's IndexedDB (D62). Nothing secret goes anywhere else.
 * - While unlocked, the decrypted secret lives only in a module variable below — not in React
 *   state, not in the zustand store, not in localStorage. Reloading or closing the tab locks.
 * - Auto-lock after inactivity (Settings: 1/5/15/30 min, default 5, D61) and on logout.
 * - Only PUBLIC addresses (`accounts`) are shared with the rest of the app (balances, receive).
 * - Recovery (Phase 9c, D68–D71): the secret is SEALED here, on the device, for the owner's offline
 *   recovery key and only that ciphertext is uploaded. On by default; existing vaults are backed up
 *   automatically at unlock unless the user turned recovery off.
 */
import type { NetworkMode, VaultRecoveryInfo } from "@custos/api-client";
import {
  addPrivateKey,
  AutoLockTimer,
  beginRestore,
  finishRestore,
  savePendingRestore,
  sealVaultBackup,
  changeVaultPassword,
  createVault,
  IndexedDbVaultStore,
  removeVault,
  unlockVault,
  type KeyFamily,
  type VaultAccount,
  type VaultKind,
  type VaultSecret,
  type VaultStore,
} from "@custos/vault";
import { create } from "zustand";
import { api, queryClient } from "./api";
import { usePrefs } from "./prefs";

export type VaultStatus = "loading" | "unavailable" | "none" | "locked" | "unlocked";

/** The decrypted secret. Only this module can read it; cleared on lock. */
let secret: VaultSecret | null = null;
let store: VaultStore | null = null;

function getStore(): VaultStore {
  store ??= new IndexedDbVaultStore();
  return store;
}

interface VaultState {
  mode: NetworkMode | null;
  status: VaultStatus;
  kind: VaultKind | null;
  families: KeyFamily[];
  /** Public addresses — safe to show and to send to the API for balances. */
  accounts: VaultAccount[];
  load: (mode: NetworkMode) => Promise<void>;
  create: (secret: VaultSecret, password: string) => Promise<void>;
  unlock: (password: string) => Promise<void>;
  lock: () => void;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  addKey: (password: string, family: KeyFamily, input: string) => Promise<void>;
  remove: () => Promise<void>;
  /** Recovery finished on this device: the restored vault is now open. */
  adopt: (unlocked: { secret: VaultSecret; accounts: VaultAccount[] }) => void;
}

function requireMode(mode: NetworkMode | null): NetworkMode {
  if (!mode) throw new Error("Vault not ready yet");
  return mode;
}

const timer = new AutoLockTimer(usePrefs.getState().vaultAutoLockMinutes * 60_000, () =>
  useVault.getState().lock(),
);
const ACTIVITY = ["pointerdown", "keydown", "wheel", "touchstart"] as const;
const onActivity = () => timer.touch();
const onVisible = () => {
  if (document.visibilityState === "visible") timer.check();
};

function startAutoLock() {
  for (const e of ACTIVITY) window.addEventListener(e, onActivity, { passive: true, capture: true });
  document.addEventListener("visibilitychange", onVisible);
  timer.touch();
}

function stopAutoLock() {
  timer.stop();
  for (const e of ACTIVITY) window.removeEventListener(e, onActivity, { capture: true });
  document.removeEventListener("visibilitychange", onVisible);
}

usePrefs.subscribe((s, prev) => {
  if (s.vaultAutoLockMinutes !== prev.vaultAutoLockMinutes)
    timer.setTimeoutMs(s.vaultAutoLockMinutes * 60_000);
});

export const useVault = create<VaultState>((set, get) => {
  const opened = (unlocked: { secret: VaultSecret; accounts: VaultAccount[] }) => {
    secret = unlocked.secret;
    set({
      status: "unlocked",
      kind: unlocked.secret.kind,
      families: unlocked.accounts.map((a) => a.family),
      accounts: unlocked.accounts,
    });
    startAutoLock();
  };

  return {
    mode: null,
    status: "loading",
    kind: null,
    families: [],
    accounts: [],

    async load(mode) {
      if (get().mode === mode && get().status !== "loading") return;
      set({ mode });
      try {
        const stored = await getStore().get(mode);
        if (get().status === "unlocked") return;
        set(
          stored
            ? { status: "locked", kind: stored.kind, families: stored.families, accounts: [] }
            : { status: "none", kind: null, families: [], accounts: [] },
        );
      } catch {
        // Private windows / blocked site data: no IndexedDB.
        set({ status: "unavailable" });
      }
    },

    async create(next, password) {
      opened(await createVault(getStore(), requireMode(get().mode), next, password));
    },

    async unlock(password) {
      opened(await unlockVault(getStore(), requireMode(get().mode), password));
      void syncRecoveryBackup();
    },

    lock() {
      secret = null;
      stopAutoLock();
      queryClient.removeQueries({ queryKey: ["vault-balances"] });
      const { status } = get();
      if (status === "unlocked") set({ status: "locked", accounts: [] });
    },

    async changePassword(oldPassword, newPassword) {
      await changeVaultPassword(getStore(), requireMode(get().mode), oldPassword, newPassword);
    },

    async addKey(password, family, input) {
      opened(await addPrivateKey(getStore(), requireMode(get().mode), password, family, input));
      void syncRecoveryBackup({ replace: true });
    },

    async remove() {
      await removeVault(getStore(), requireMode(get().mode));
      get().lock();
      set({ status: "none", kind: null, families: [], accounts: [] });
    },

    adopt(unlocked) {
      opened(unlocked);
    },
  };
});

/**
 * Re-reads the secret WITH the password (for "Show recovery phrase"). Deliberately does not use
 * the in-memory copy, so someone at an unlocked screen still can't see it without the password.
 */
export async function revealVaultSecret(password: string): Promise<VaultSecret> {
  const { mode } = useVault.getState();
  return (await unlockVault(getStore(), requireMode(mode), password)).secret;
}

/** For Phase 9b (signing). Throws when locked. */
export function currentVaultSecret(): VaultSecret {
  if (!secret) throw new Error("The vault is locked");
  return secret;
}

// Logging out (or the session ending) locks the vault too.
api.onAuthChange((loggedIn) => {
  if (!loggedIn) useVault.getState().lock();
});

// ───────────────────────── Recovery (Phase 9c) ─────────────────────────

export const vaultRecoveryKey = ["vault-recovery"] as const;

async function myUserId(): Promise<string> {
  const me = await queryClient.fetchQuery({ queryKey: ["me"], queryFn: () => api.me(), staleTime: 60_000 });
  return me.id;
}

function addressesOf(accounts: VaultAccount[]): Partial<Record<KeyFamily, string>> {
  return Object.fromEntries(accounts.map((a) => [a.family, a.address]));
}

/** Same vault? (compares the public addresses only) */
export function sameVault(
  a: Partial<Record<KeyFamily, string>> | null | undefined,
  accounts: VaultAccount[],
) {
  if (!a) return false;
  const b = addressesOf(accounts);
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].every((k) => a[k as KeyFamily] === b[k as KeyFamily]);
}

/**
 * Uploads the sealed backup when recovery should be on for this vault (D70):
 * - no backup yet (new or older vault)        → back it up (recovery is on by default)
 * - the user turned recovery off              → leave it off
 * - a backup of a DIFFERENT vault exists      → only with replace: true (the user is asked)
 * Only ciphertext leaves the device. Never throws; the result is in the "vault-recovery" query.
 */
export async function syncRecoveryBackup(opts: { replace?: boolean; turnOn?: boolean } = {}): Promise<void> {
  const { mode, accounts } = useVault.getState();
  if (!secret || !mode) return;
  try {
    const info = await api.vaultRecovery();
    queryClient.setQueryData(vaultRecoveryKey, info);
    if (!info.available || !info.publicKey) return;
    const backup = info.backup;
    if (backup && !backup.enabled && !opts.turnOn) return;
    if (backup?.enabled && sameVault(backup.addresses, accounts) && backup.kind === secret.kind) return;
    if (backup?.enabled && !sameVault(backup.addresses, accounts) && !opts.replace) return;
    const sealed = sealVaultBackup(secret, info.publicKey, mode, await myUserId());
    const next = await api.vaultSaveBackup({ sealed, kind: secret.kind, addresses: addressesOf(accounts) });
    queryClient.setQueryData(vaultRecoveryKey, next);
  } catch {
    await queryClient.invalidateQueries({ queryKey: vaultRecoveryKey });
  }
}

export async function disableRecovery(): Promise<VaultRecoveryInfo> {
  const info = await api.vaultDisableRecovery();
  queryClient.setQueryData(vaultRecoveryKey, info);
  return info;
}

/** Asks support to recover the vault onto THIS device, protected by a new vault password. */
export async function startRecovery(newPassword: string, totpCode: string): Promise<void> {
  const mode = requireMode(useVault.getState().mode);
  const key = beginRestore(newPassword);
  try {
    const request = await api.vaultRequestRecovery({ devicePublicKey: key.publicKeyHex, totpCode });
    await savePendingRestore(getStore(), mode, await myUserId(), request.id, key, newPassword);
  } finally {
    key.secretKey.fill(0);
    await queryClient.invalidateQueries({ queryKey: vaultRecoveryKey });
  }
}

/** Does THIS device hold the one-time key for a recovery in progress? */
export async function hasPendingRestore(): Promise<boolean> {
  const mode = requireMode(useVault.getState().mode);
  return (await getStore().getRestore(mode)) !== null;
}

/** After approval + waiting period: collect the box, restore the vault here, confirm. */
export async function finishRecovery(requestId: string, password: string): Promise<void> {
  const mode = requireMode(useVault.getState().mode);
  const { restoreBox } = await api.vaultRecoveryBox(requestId);
  const unlocked = await finishRestore(getStore(), mode, password, restoreBox);
  useVault.getState().adopt(unlocked);
  await api.vaultCompleteRecovery(requestId);
  await queryClient.invalidateQueries({ queryKey: vaultRecoveryKey });
}
